<script setup lang="ts">
import {type PropType} from 'vue';
import {RouterLink, useRoute} from 'vue-router';
import ZZCMark from '@/views/header/ZZCMark.vue';
import CurrentUser from '@/views/header/CurrentUser.vue';
import {getAppVersion} from '@/base/frontendConfig.js';
import {BADGE_CLASS, type NavItem} from '@/views/shell/navItems.js';

// 48px of icons; from lg up the rail widens to 200px on hover and shows the labels, below that it stays
// collapsed and the title attributes are the only labels. The content follows through the padding in
// AppShell, so nothing is ever covered
defineProps({
    items: {type: Array as PropType<NavItem[]>, required: true},
});

const route = useRoute();

function isActive(item: NavItem): boolean {
    return item.exact === false ? route.path.startsWith(item.to) : route.path === item.to;
}

// a 0 shows only next to a sibling badge that does show something, as CabLink did
function badgeText(n: number | undefined, sibling?: number): string {
    if (n === undefined || n === -1) {
        return '';
    }
    if (n === 0) {
        return sibling ? '0' : '';
    }
    return String(n);
}
</script>

<template>
    <nav
        class="group fixed top-0 left-0 h-screen z-50 bg-zzc-blauw text-white flex flex-col py-3 px-1.5 gap-1 w-12 lg:hover:w-[200px] transition-[width] duration-200 overflow-x-hidden"
        aria-label="navigatie"
    >
        <div class="flex items-center gap-2 h-9 mb-3 whitespace-nowrap">
            <span class="w-9 h-9 rounded-full bg-white flex items-center justify-center shrink-0"><ZZCMark :size="26"/></span>
            <span class="font-semibold opacity-0 lg:group-hover:opacity-100 transition-opacity">Zeeuwse Zorgcoalitie</span>
        </div>
        <!-- a block-level line follows the rail width, so it grows with the hover expansion -->
        <div class="h-px bg-white/20 mx-1 mb-3 shrink-0"/>

        <div class="flex flex-col gap-1 overflow-y-auto overflow-x-hidden min-h-0">
            <RouterLink
                v-for="item in items"
                :key="item.to"
                :to="item.to"
                class="relative flex items-center gap-3 rounded-control px-2 py-2 whitespace-nowrap text-sm transition-colors"
                :class="isActive(item) ? 'bg-white text-zzc-blauw font-semibold' : 'text-white/85 hover:bg-white/15'"
                :title="item.label"
                :aria-label="item.label"
            >
                <span class="relative shrink-0" :class="isActive(item) ? 'text-zzc-groen' : ''">
                    <component :is="item.icon" :size="18" :stroke-width="2"/>
                    <span v-if="badgeText(item.badge, item.badgeExtra)"
                          class="absolute -top-2 -right-2 min-w-4 h-4 px-1 rounded-full text-[10px] leading-4 text-center font-semibold"
                          :class="BADGE_CLASS[item.badgeColor ?? 'green']">{{ badgeText(item.badge, item.badgeExtra) }}</span>
                    <span v-if="badgeText(item.badgeExtra)"
                          class="absolute -bottom-2 -right-2 min-w-4 h-4 px-1 rounded-full text-[10px] leading-4 text-center font-semibold"
                          :class="BADGE_CLASS[item.badgeExtraColor ?? 'orange']">{{ badgeText(item.badgeExtra) }}</span>
                </span>
                <span class="opacity-0 lg:group-hover:opacity-100 transition-opacity">{{ item.label }}</span>
            </RouterLink>
        </div>

        <div class="mt-auto flex flex-col gap-1">
            <div class="px-2 text-[10px] text-white/60 whitespace-nowrap opacity-0 lg:group-hover:opacity-100 transition-opacity">{{ getAppVersion() }}</div>
            <CurrentUser/>
        </div>
    </nav>
</template>
